import * as THREE from 'three';
import { countGeometryStats } from '../core/math';
import { createNacreMaterial, createStoneMaterial } from '../shaders/materials';
import {
  createNautilusShellGeometry,
  createTurritellaShellGeometry,
} from './seashells';
import type { InteractiveElement, SpecimenInstance, TemporalUniforms } from '../core/types';

export interface SpiralTowerConfig {
  radius: number;
  height: number;
  turns: number;
  stepsCount: number;
  clockwise?: boolean;
  seedOffset?: number;
  crownType?: 'nautilus-dome' | 'lantern-cupola' | 'open-terrace';
  sharedNautilusGeo?: THREE.BufferGeometry;
  sharedTurritellaGeo?: THREE.BufferGeometry;
}

export interface SpiralTowerResult {
  group: THREE.Group;
  shellCount: number;
  lanternCount: number;
  interactables: InteractiveElement[];
  update: (elapsed: number, temporal: number) => void;
}

/**
 * Generates an architectural tower wrapped by an external cantilevered
 * logarithmic/helicoidal staircase encrusted with iridescent seashells.
 */
export function createSpiralStaircaseTower(
  config: SpiralTowerConfig,
  uniforms: TemporalUniforms
): SpiralTowerResult {
  const group = new THREE.Group();
  const interactables: InteractiveElement[] = [];

  const {
    radius,
    height,
    turns,
    stepsCount,
    clockwise = true,
    seedOffset = 0,
    crownType = 'nautilus-dome',
  } = config;
  const dir = clockwise ? 1 : -1;

  const towerStoneMat = createStoneMaterial(uniforms, {
    baseDesireHex: 0x68464f,
    baseMemoryHex: 0x26344e,
    roughness: 0.66,
    metalness: 0.14,
  });

  const stepStoneMat = createStoneMaterial(uniforms, {
    baseDesireHex: 0x8a5f62,
    baseMemoryHex: 0x384b6b,
    roughness: 0.54,
    metalness: 0.18,
  });

  const brassMat = new THREE.MeshStandardMaterial({
    color: 0xdba558,
    roughness: 0.26,
    metalness: 0.85,
  });

  const windowGlowMat = new THREE.MeshBasicMaterial({
    color: 0xffb85c,
  });

  const nacreMatA = createNacreMaterial(uniforms, seedOffset * 0.7);
  const nacreMatB = createNacreMaterial(uniforms, seedOffset * 0.7 + 1.6);

  // 1. Stepped cylindrical masonry core with molded cornices
  const tiers = 3;
  for (let t = 0; t < tiers; t++) {
    const tierFrac0 = t / tiers;
    const tierFrac1 = (t + 1) / tiers;
    const tierH = (tierFrac1 - tierFrac0) * height;
    const rBottom = radius * (1.0 - tierFrac0 * 0.18);
    const rTop = radius * (1.0 - tierFrac1 * 0.22);

    const cylGeo = new THREE.CylinderGeometry(rTop, rBottom, tierH, 28, 1, false);
    const cylMesh = new THREE.Mesh(cylGeo, towerStoneMat);
    cylMesh.position.y = tierFrac0 * height + tierH * 0.5;
    cylMesh.castShadow = true;
    cylMesh.receiveShadow = true;
    group.add(cylMesh);

    const corniceGeo = new THREE.TorusGeometry(rTop * 1.03, 0.11, 10, 36);
    corniceGeo.rotateX(Math.PI / 2);
    const cornice = new THREE.Mesh(corniceGeo, stepStoneMat);
    cornice.position.y = tierFrac1 * height;
    group.add(cornice);
  }

  // 2. Warm arched windows around the masonry shaft
  let lanternCount = 0;
  const windowRows = Math.max(2, Math.floor(height / 3.8));
  for (let row = 0; row < windowRows; row++) {
    const wy = (row + 0.55) * (height / windowRows);
    const rAtY = radius * (1.0 - (wy / height) * 0.2);
    const winPerRow = 5;
    for (let w = 0; w < winPerRow; w++) {
      const ang = (w / winPerRow) * Math.PI * 2 + row * 0.6 + seedOffset;
      const winGeo = new THREE.BoxGeometry(0.32, 0.78, 0.12);
      const winMesh = new THREE.Mesh(winGeo, windowGlowMat);
      winMesh.position.set(
        Math.cos(ang) * (rAtY - 0.02),
        wy,
        Math.sin(ang) * (rAtY - 0.02)
      );
      winMesh.lookAt(0, wy, 0);
      group.add(winMesh);
      lanternCount++;
    }
  }

  // 3. External Cantilevered Spiral Staircase
  const stepGeo = new THREE.BoxGeometry(1.15, 0.16, 0.52);
  const stepsInstanced = new THREE.InstancedMesh(stepGeo, stepStoneMat, stepsCount);
  stepsInstanced.castShadow = true;
  stepsInstanced.receiveShadow = true;

  const postGeo = new THREE.CylinderGeometry(0.032, 0.042, 0.72, 8);
  const postsInstanced = new THREE.InstancedMesh(postGeo, brassMat, stepsCount);

  const railPoints: THREE.Vector3[] = [];
  const outerStringerPoints: THREE.Vector3[] = [];
  const dummy = new THREE.Object3D();

  for (let i = 0; i < stepsCount; i++) {
    const frac = i / (stepsCount - 1);
    const theta = dir * frac * turns * Math.PI * 2 + seedOffset;
    const y = frac * height * 0.96 + 0.2;
    const coreR = radius * (1.0 - frac * 0.2);
    const stairMidR = coreR + 0.52;
    const stairOuterR = coreR + 1.02;

    dummy.position.set(Math.cos(theta) * stairMidR, y, Math.sin(theta) * stairMidR);
    dummy.rotation.set(0, -theta, 0);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    stepsInstanced.setMatrixAt(i, dummy.matrix);

    dummy.position.set(
      Math.cos(theta) * stairOuterR,
      y + 0.36,
      Math.sin(theta) * stairOuterR
    );
    dummy.rotation.set(0, 0, 0);
    dummy.updateMatrix();
    postsInstanced.setMatrixAt(i, dummy.matrix);

    railPoints.push(
      new THREE.Vector3(
        Math.cos(theta) * stairOuterR,
        y + 0.72,
        Math.sin(theta) * stairOuterR
      )
    );
    outerStringerPoints.push(
      new THREE.Vector3(
        Math.cos(theta) * (stairOuterR + 0.05),
        y - 0.04,
        Math.sin(theta) * (stairOuterR + 0.05)
      )
    );
  }

  group.add(stepsInstanced);
  group.add(postsInstanced);

  const railCurve = new THREE.CatmullRomCurve3(railPoints);
  const railGeo = new THREE.TubeGeometry(railCurve, stepsCount * 2, 0.042, 8, false);
  const railMesh = new THREE.Mesh(railGeo, brassMat);
  group.add(railMesh);

  const stringerCurve = new THREE.CatmullRomCurve3(outerStringerPoints);
  const stringerGeo = new THREE.TubeGeometry(stringerCurve, stepsCount * 2, 0.14, 10, false);
  const stringerMesh = new THREE.Mesh(stringerGeo, stepStoneMat);
  group.add(stringerMesh);

  // 4. Encrusted Iridescent Seashells along the spiral stringer
  const nautilusGeo = config.sharedNautilusGeo ?? createNautilusShellGeometry(0.32, 0.85);
  const turritellaGeo = config.sharedTurritellaGeo ?? createTurritellaShellGeometry(0.36, 0.85);

  const nautilusCount = Math.max(10, Math.floor(stepsCount / 2.5));
  const turritellaCount = Math.max(10, Math.floor(stepsCount / 2.5));

  const nautilusInstanced = new THREE.InstancedMesh(nautilusGeo, nacreMatA, nautilusCount);
  const turritellaInstanced = new THREE.InstancedMesh(turritellaGeo, nacreMatB, turritellaCount);

  for (let i = 0; i < nautilusCount; i++) {
    const frac = (i + 0.3) / nautilusCount;
    const pt = stringerCurve.getPointAt(frac);
    const tangent = stringerCurve.getTangentAt(frac);
    const radialOut = new THREE.Vector3(pt.x, 0, pt.z).normalize();

    dummy.position.copy(pt).addScaledVector(radialOut, 0.18);
    dummy.lookAt(pt.clone().add(tangent));
    dummy.rotateZ(i * 0.7);
    const sc = 0.75 + 0.45 * Math.sin(i * 2.1 + seedOffset);
    dummy.scale.setScalar(sc);
    dummy.updateMatrix();
    nautilusInstanced.setMatrixAt(i, dummy.matrix);
  }

  for (let i = 0; i < turritellaCount; i++) {
    const frac = (i + 0.75) / turritellaCount;
    const pt = stringerCurve.getPointAt(Math.min(frac, 0.99));
    const radialOut = new THREE.Vector3(pt.x, 0, pt.z).normalize();

    dummy.position.copy(pt).addScaledVector(radialOut, 0.2);
    dummy.position.y += 0.18;
    dummy.rotation.set(0.35 * Math.cos(i), -Math.atan2(pt.z, pt.x), 0.25);
    const sc = 0.7 + 0.45 * Math.cos(i * 1.7 + seedOffset);
    dummy.scale.setScalar(sc);
    dummy.updateMatrix();
    turritellaInstanced.setMatrixAt(i, dummy.matrix);
  }

  group.add(nautilusInstanced);
  group.add(turritellaInstanced);

  let shellCount = nautilusCount + turritellaCount;

  // 5. Interactive Nautilus Crest at mid-flight landing & tower crown
  const heroShellGeo = createNautilusShellGeometry(0.55, 1.0);
  const heroShellMat = createNacreMaterial(uniforms, seedOffset + 0.5);
  const heroShellMesh = new THREE.Mesh(heroShellGeo, heroShellMat);
  const midPt = stringerCurve.getPointAt(0.52);
  const midOut = new THREE.Vector3(midPt.x, 0, midPt.z).normalize();
  heroShellMesh.position.copy(midPt).addScaledVector(midOut, 0.28);
  heroShellMesh.rotation.y = -Math.atan2(midPt.z, midPt.x) + Math.PI * 0.5;
  group.add(heroShellMesh);
  shellCount += 1;

  interactables.push({
    object: heroShellMesh,
    kind: 'seashell',
    title: 'Encrusted Nautilus Conch',
    detail: 'Logarithmic spiral shell embedded in the helical balustrade — click to hear its cavity resonance.',
    zhTitle: '鑲嵌之鸚鵡螺',
    zhDetail: '鑲嵌於螺旋欄杆上的對數螺旋海螺——點擊聆聽海螺腔體共鳴。',
    motif: 'seashells',
    frequency: 293.66 * (1 + (seedOffset % 3) * 0.125),
    onHover: (hovered) => {
      heroShellMat.uniforms.uHover.value = hovered ? 1.0 : 0.0;
    },
  });

  if (crownType === 'nautilus-dome') {
    const crownShell = new THREE.Mesh(heroShellGeo, nacreMatA);
    crownShell.position.set(0, height + 0.65, 0);
    crownShell.scale.setScalar(1.45);
    group.add(crownShell);
    shellCount += 1;
  } else if (crownType === 'lantern-cupola') {
    const cupolaGeo = new THREE.SphereGeometry(radius * 0.62, 24, 16, 0, Math.PI * 2, 0, Math.PI * 0.6);
    const cupola = new THREE.Mesh(cupolaGeo, stepStoneMat);
    cupola.position.y = height;
    group.add(cupola);
  }

  const desireWinColor = new THREE.Color(0xffb858);
  const memoryWinColor = new THREE.Color(0x64a8ff);

  return {
    group,
    shellCount,
    lanternCount,
    interactables,
    update: (elapsed, temporal) => {
      windowGlowMat.color.copy(desireWinColor).lerp(memoryWinColor, temporal * 0.85);
      heroShellMesh.rotation.z = Math.sin(elapsed * 0.8 + seedOffset) * 0.08;
    },
  };
}

export function buildSpiralTowerSpecimen(uniforms: TemporalUniforms): SpecimenInstance {
  const tower = createSpiralStaircaseTower(
    {
      radius: 1.35,
      height: 5.0,
      turns: 2.0,
      stepsCount: 44,
      clockwise: true,
      seedOffset: 1.2,
      crownType: 'nautilus-dome',
    },
    uniforms
  );
  const stats = countGeometryStats(tower.group);

  return {
    group: tower.group,
    update: (elapsed, _dt, temporal) => {
      tower.update(elapsed, temporal);
    },
    dispose: () => {},
    meta: {
      vertices: stats.vertices,
      triangles: stats.triangles,
      formula: 'P(θ) = ( (R₀ − αθ)cosθ,  h·θ/(2πN),  (R₀ − αθ)sinθ )',
      parameters: {
        'Helical Turns (N)': 2.0,
        'Cantilevered Treads': 44,
        'Encrusted Shells': tower.shellCount,
        'Masonry Material': 'Carved Travertine + Nacre Inlay',
      },
    },
  };
}
